import { gsap, CustomEase, ScrollTrigger } from '../../js/gsap.js';
import lenis from '../../js/scroll.js';
import { fadeUp, revealLines } from '../../js/utils/reveal.js';

CustomEase.create('osmo', 'M0,0 C0.625,0.05 0,1 1,1');

const reveals = {
  headerLogo: '[data-reveal="header-logo"]',
  headerNav: '[data-reveal="header-nav"]',
  headerActions: '[data-reveal="header-actions"]',
  heroEyebrow: '[data-reveal="hero-eyebrow"]',
  heroTitle: '[data-reveal="hero-title"]',
  heroActions: '[data-reveal="hero-actions"]',
  heroSlogan: '[data-reveal="hero-slogan"]',
  heroCaption: '[data-reveal="hero-caption"]'
};

const selectors = {
  container: '[data-loading-container]',
  cardsList: '[data-loading-cards-list]',
  card: '[data-loading-card]',
  background: '[data-loading-background]',
  logo: '[data-loading-logo]',
  media: '[data-loading-media]'
};

const defaults = {
  scaleStep: 0.1,
  stackOffset: -7.5,
  stackStagger: -0.03,
  fallStagger: 0.4,
  deckDuration: 1
};

const rotationPattern = [-10, 10, -15, 10, 20];
const xPattern = [-5, 7.5, 10, 5, -10];

/**
 * Reads one tunable off the container's data attributes. Falls back to the
 * module default when the attribute is absent or not a number, so the markup
 * only has to spell out the values it actually wants to change.
 *
 * @param {DOMStringMap} dataset Dataset of the loading container.
 * @param {string} key Camel-cased attribute name, e.g. 'fallStagger'.
 * @param {number} fallback Value to use when the attribute is unusable.
 * @returns {number}
 */

function readNumber(dataset, key, fallback) {
  const value = Number.parseFloat(dataset[key]);

  return Number.isFinite(value) ? value : fallback;
}

function readSettings({ dataset }) {
  return {
    scaleStep: readNumber(dataset, 'scaleStep', defaults.scaleStep),
    stackOffset: readNumber(dataset, 'stackOffset', defaults.stackOffset),
    stackStagger: readNumber(dataset, 'stackStagger', defaults.stackStagger),
    fallStagger: readNumber(dataset, 'fallStagger', defaults.fallStagger),
    deckDuration: readNumber(dataset, 'deckDuration', defaults.deckDuration)
  };
}

function initDroppingCardsLoadingAnimation() {
  const container = document.querySelector(selectors.container);

  if (!container) return null;

  const settings = readSettings(container);

  const cardsList = gsap.utils.toArray(container.querySelectorAll(selectors.cardsList));
  const cards = gsap.utils.toArray(container.querySelectorAll(selectors.card));
  const background = container.querySelectorAll(selectors.background);
  const logo = container.querySelectorAll(selectors.logo);
  const media = document.querySelectorAll(selectors.media);

  const has = (items) => items.length;
  const patternValue = (pattern, index) => pattern[index % pattern.length];

  function getStack(index, total) {
    const reverseIndex = total - 1 - index;

    return {
      scale: 1 - reverseIndex * settings.scaleStep,
      yPercent: reverseIndex * settings.stackOffset
    };
  }

  function stackProp(prop, total) {
    return (index) => getStack(index, total)[prop];
  }

  function getFallY(card) {
    const containerRect = container.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();

    return containerRect.bottom - cardRect.top + cardRect.height;
  }

  const tl = gsap.timeline();

  if (has(cardsList)) {
    tl.fromTo(cardsList, { opacity: 0 }, { opacity: 1, duration: 0.3 }, 0.5);
  }

  if (has(cards)) {
    tl.fromTo(
      cards,
      {
        rotate: 0.001,
        scale: 0.5,
        yPercent: 0
      },
      {
        rotate: 0.001,
        scale: stackProp('scale', cards.length),
        yPercent: stackProp('yPercent', cards.length),
        stagger: settings.stackStagger,
        duration: 1.5,
        ease: 'elastic.out(1,0.7)'
      },
      '<'
    );

    const fallCards = cards.slice().reverse();
    const fallStagger = settings.fallStagger / Math.max(cards.length - 1, 1);
    const fallStart = tl.duration();

    fallCards.forEach((card, fallIndex) => {
      const remainingCards = cards.slice(0, cards.indexOf(card));
      const fallTime = fallStart + fallIndex * fallStagger;

      if (has(remainingCards)) {
        tl.to(
          remainingCards,
          {
            scale: stackProp('scale', remainingCards.length),
            yPercent: stackProp('yPercent', remainingCards.length),
            duration: settings.deckDuration,
            ease: 'sine.inOut'
          },
          fallTime
        );
      }

      tl.to(
        card,
        {
          y: () => getFallY(card),
          xPercent: patternValue(xPattern, fallIndex),
          rotate: patternValue(rotationPattern, fallIndex),
          duration: 0.8,
          ease: 'power4.in'
        },
        fallTime
      );
    });
  }

  if (has(background)) {
    tl.to(
      background,
      {
        rotate: 0.001,
        yPercent: 100,
        duration: 1.5,
        ease: 'osmo'
      },
      '-=0.6'
    );
  }

  if (has(media)) {
    tl.from(
      media,
      {
        rotate: 0.001,
        scale: 1.1,
        duration: 1.5,
        ease: 'osmo'
      },
      '<'
    );
  }

  if (has(logo)) {
    tl.to(
      logo,
      {
        rotate: 0.001,
        yPercent: 100,
        opacity: 0,
        duration: 0.8,
        ease: 'power4.in'
      },
      '<-=1.5'
    );
  }

  return tl;
}

function initIntro() {
  const timeline = initDroppingCardsLoadingAnimation();

  if (!timeline) return;

  lenis.stop();

  // Triggers measured while the loader holds the page are stale: the reveals are
  // still hidden and the hero media is mid-tween. Re-measure once scroll is free.

  timeline.eventCallback('onComplete', () => {
    lenis.start();
    ScrollTrigger.refresh();
  });

  // ─── Reveal stack ─────────────────────────────────────────────────────────
  // One line per element, in the order it shows up. The last argument is that
  // element's own start on the loader timeline, in seconds — move it freely,
  // the lines do not depend on each other.

  fadeUp(timeline, reveals.headerLogo, 3);
  fadeUp(timeline, reveals.headerNav, 3.1);
  fadeUp(timeline, reveals.headerActions, 3.2);

  fadeUp(timeline, reveals.heroEyebrow, 3.1);
  revealLines(timeline, reveals.heroTitle, 3.2);
  fadeUp(timeline, reveals.heroActions, 3.3);
  revealLines(timeline, reveals.heroSlogan, 3.4);
  fadeUp(timeline, reveals.heroCaption, 3.5);
}

// Fonts settle before SplitText measures the hero, otherwise the lines re-wrap.

document.fonts.ready.then(initIntro);
